import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router-dom";
import styles from "./markdown.module.scss";

const isInternalHref = (href?: string): href is string =>
  !!href && href.startsWith("/") && !href.startsWith("//");

const MarkdownLink: React.FC<{
  href?: string;
  children: React.ReactNode;
}> = ({ href, children }) =>
  isInternalHref(href) ? (
    <Link to={href} className={styles.link}>
      {children}
    </Link>
  ) : (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.link}
    >
      {children}
    </a>
  );

// Treat single newlines as hard line breaks (like remark-breaks)
const remarkBreaks = () => (tree: any) => {
  const visit = (node: any) => {
    if (node.children) {
      node.children = node.children.flatMap((child: any) => {
        if (child.type === "text" && child.value.includes("\n")) {
          const parts = child.value.split("\n");
          const result: any[] = [];
          parts.forEach((part: string, i: number) => {
            if (i > 0) result.push({ type: "break" });
            if (part) result.push({ type: "text", value: part });
          });
          return result;
        }
        visit(child);
        return child;
      });
    }
  };
  visit(tree);
};

const SHORT_ID_REGEX = /\b([ANLT]\d{7})\b/g;

function shortIdToPath(id: string): string {
  switch (id[0]) {
    case "A":
      return `/audit/${id}`;
    case "N":
      return `/notices/${id}`;
    case "L":
      return `/litigations/${id}`;
    case "T":
      return `/task/${id}`;
    default:
      return id;
  }
}

const remarkGaiaShortIds = () => (tree: any) => {
  const visit = (node: any) => {
    if (!node.children) return;
    node.children = node.children.flatMap((child: any) => {
      if (child.type === "link") return [child];
      if (child.type !== "text") {
        visit(child);
        return [child];
      }
      const parts: any[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      SHORT_ID_REGEX.lastIndex = 0;
      while ((match = SHORT_ID_REGEX.exec(child.value)) !== null) {
        if (match.index > lastIndex) {
          parts.push({
            type: "text",
            value: child.value.slice(lastIndex, match.index),
          });
        }
        parts.push({
          type: "link",
          url: shortIdToPath(match[1]),
          children: [{ type: "text", value: match[1] }],
        });
        lastIndex = SHORT_ID_REGEX.lastIndex;
      }
      if (parts.length === 0) return [child];
      if (lastIndex < child.value.length) {
        parts.push({ type: "text", value: child.value.slice(lastIndex) });
      }
      return parts;
    });
  };
  visit(tree);
};

interface MarkdownRendererProps {
  content: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  return (
    <div className={styles.markdown} data-copy-target>
      <ReactMarkdown
        remarkPlugins={[
          [remarkGfm, { singleTilde: false }],
          remarkBreaks,
          remarkGaiaShortIds,
        ]}
        components={{
          p: ({ children }) => <p className={styles.paragraph}>{children}</p>,
          ul: ({ children }) => <ul className={styles.list}>{children}</ul>,
          ol: ({ children }) => <ol className={styles.list}>{children}</ol>,
          a: ({ href, children }) => (
            <MarkdownLink href={href}>{children}</MarkdownLink>
          ),
          pre: ({ children }) => (
            <pre className={styles.codeBlock}>{children}</pre>
          ),
          code: ({ children, className }) => {
            const isBlock = className?.startsWith("language-");
            return isBlock ? (
              <code>{children}</code>
            ) : (
              <code className={styles.inlineCode}>{children}</code>
            );
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
